import { useState, useRef, useCallback, useEffect } from "react";
import {
  FieldFormData,
  FieldFormState,
  OptionsManagerData,
  OptionItem,
  initializeFieldFormData
} from "../utils/fieldFormUtils";
import { APIFieldItem } from "@/pages/settings/types";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import {
  useUpdateEditableSystemFieldOptions,
  useLookupBulkUpdate,
  useLookupValueUsage,
  useUpdateEntityField,
  EditableSystemFieldOption
} from "@/pages/settings/hooks";
import { NONE_OPTION_VALUE } from "@/components/controls/Form/Form";
import { getLookupFieldConfig } from "../lookupFieldRegistry";
import { RenameStrategy } from "../components/RenameValueModal";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { snakeCase } from "lodash";
import { FieldDisplayType } from "../types";

interface UseEditableSystemFieldFormProps {
  fieldData?: APIFieldItem | null;
  entityType?: EntityType;
}

interface UseEditableSystemFieldFormReturn {
  formState: FieldFormState;
  currentFieldData: APIFieldItem | null;
  isFormValid: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  formSetValue: any;
  setFormData: (data: FieldFormData) => void;
  setOptionsManagerData: (data: OptionsManagerData) => void;
  setIsRequired: (required: boolean) => void;
  setShowInCreation: (show: boolean) => void;
  setShowHelpText: (show: boolean) => void;
  setFormSetValue: (setValue: any) => void;
  handleFormValid: (valid: boolean, data: any) => void;
  handleOptionsManagerChange: (data: OptionsManagerData) => void;
  handleHiddenToggle: (id: number | string) => void;
  handleFormSubmit: () => Promise<void>;
  handleSubmitButton: () => void;
  isSubmitting: boolean;
  submitError: Error | null;
  // Non-null while a save is paused on the rename-in-use confirmation.
  renamePrompt: { oldName: string; newName: string } | null;
  resolveRename: (strategy: RenameStrategy | "cancel") => void;
}

// Thrown to abort a save on rename-prompt cancel; the drawer swallows it (no toast).
export const RENAME_CANCELLED = "RENAME_CANCELLED";

export const useEditableSystemFieldForm = ({
  fieldData,
  entityType = EntityType.DEAL
}: UseEditableSystemFieldFormProps): UseEditableSystemFieldFormReturn => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState(true);
  const [formSetValue, setFormSetValue] = useState<any>(null);
  const { enabled: defaultEntryEnabled, loading: defaultEntryLoading } =
    useFeatureFlag(FeatureFlags.CRM_5207_SYSTEM_FIELD_DEFAULT_ENTRY);
  // Flag gates isHidden in the payload + the rename-in-use confirmation.
  const {
    enabled: configurableSystemFieldsEnabled,
    loading: configurableSystemFieldsLoading
  } = useFeatureFlag(FeatureFlags.CRM_5705_CONFIGURABLE_SYSTEM_FIELDS);

  const [formState, setFormState] = useState<FieldFormState>(() =>
    initializeFieldFormData(fieldData || null, true, true)
  );

  // Registry entry (if any); drives usage lookups and the scoped save path.
  const lookupConfig = getLookupFieldConfig(fieldData?.fieldKey);

  // Only select-type fields carry an option list; phone/country have none to validate or bulk-save.
  const hasOptions =
    fieldData?.displayType === FieldDisplayType.SINGLE_SELECT ||
    fieldData?.displayType === FieldDisplayType.MULTI_SELECT;

  const updateOptionsMutation = useUpdateEditableSystemFieldOptions(entityType);
  const updateOptionsMutationRef = useRef(updateOptionsMutation);
  updateOptionsMutationRef.current = updateOptionsMutation;

  // Parent-scoped bulk save; instantiated for every field, only fired for scoped ones.
  const scopedBulkMutation = useLookupBulkUpdate(
    {
      bulkRoute: lookupConfig?.parent?.bulkUpdateRoute,
      readRoute: lookupConfig?.listRoute
    },
    entityType
  );
  const scopedBulkMutationRef = useRef(scopedBulkMutation);
  scopedBulkMutationRef.current = scopedBulkMutation;

  // Field-level settings (required / show-in-creation) persist via the per-field
  // update endpoint — the bulk endpoints don't carry them.
  const updateFieldMutation = useUpdateEntityField(entityType);
  const updateFieldMutationRef = useRef(updateFieldMutation);
  updateFieldMutationRef.current = updateFieldMutation;

  const fetchUsage = useLookupValueUsage();
  // Server-truth names by id, so a save can tell what was renamed. Re-synced
  // after each save (the drawer stays mounted across reopens).
  const baselineNamesRef = useRef<Map<number | string, string> | null>(null);
  // Set while a save is paused on the rename confirmation; resolver in a ref.
  const [renamePrompt, setRenamePrompt] = useState<{
    oldName: string;
    newName: string;
  } | null>(null);
  const renameResolverRef = useRef<
    ((strategy: RenameStrategy | "cancel") => void) | null
  >(null);

  useEffect(() => {
    setFormState(initializeFieldFormData(fieldData || null, true, true));
    // New field ⇒ drop the old baseline; it's recaptured from the next seed.
    baselineNamesRef.current = null;
  }, [fieldData]);

  const promptRename = useCallback(
    (oldName: string, newName: string): Promise<RenameStrategy | "cancel"> =>
      new Promise((resolve) => {
        renameResolverRef.current = resolve;
        setRenamePrompt({ oldName, newName });
      }),
    []
  );

  const resolveRename = useCallback(
    (strategy: RenameStrategy | "cancel") => {
      const resolve = renameResolverRef.current;
      renameResolverRef.current = null;
      setRenamePrompt(null);
      resolve?.(strategy);
    },
    []
  );

  const setFormData = useCallback((data: FieldFormData) => {
    setFormState((prev) => ({ ...prev, formData: data }));
  }, []);

  const setOptionsManagerData = useCallback((data: OptionsManagerData) => {
    setFormState((prev) => ({ ...prev, optionsManagerData: data }));
  }, []);

  const setIsRequired = useCallback((required: boolean) => {
    setFormState((prev) => ({ ...prev, isRequired: required }));
  }, []);

  const setShowInCreation = useCallback((show: boolean) => {
    setFormState((prev) => ({ ...prev, showInCreation: show }));
  }, []);

  const setShowHelpText = useCallback((show: boolean) => {
    setFormState((prev) => ({ ...prev, showHelpText: show }));
  }, []);

  const handleFormValid = useCallback(
    (_valid: boolean, data: any) => {
      const options = formState.optionsManagerData.options || [];
      const hasValidOption = options.some((option) => option.trim() !== "");
      setIsFormValid(!hasOptions || hasValidOption);
      setFormData(data);
    },
    [formState.optionsManagerData, hasOptions, setFormData]
  );

  const handleOptionsManagerChange = useCallback((data: OptionsManagerData) => {
    // Capture each id's baseline the first time it's seen (per-id, so switching
    // the sub-industry parent adds newly-loaded ids); edits never overwrite it.
    if (baselineNamesRef.current === null) {
      baselineNamesRef.current = new Map<number | string, string>();
    }
    const baseline = baselineNamesRef.current;
    (data.optionItems ?? []).forEach((item) => {
      if (item.id !== undefined && !baseline.has(item.id)) {
        baseline.set(item.id, item.name);
      }
    });
    setFormState((prev) => {
      // Panels push data.optionItems (real id + flags) — use it as source of
      // truth; for OptionsManager's name-only edits, match names to keep id/flags.
      const prevOptionItems = prev.optionsManagerData.optionItems || [];
      const nameToItemMap = new Map<string, OptionItem>();
      prevOptionItems.forEach((item) => {
        nameToItemMap.set(item.name, item);
      });

      const updatedOptionItems =
        data.optionItems ??
        data.options.map((optionName) => {
          const existingItem = nameToItemMap.get(optionName);
          if (existingItem) {
            return existingItem;
          }
          return { name: optionName };
        });

      return {
        ...prev,
        optionsManagerData: {
          ...data,
          optionItems: updatedOptionItems
        }
      };
    });
    const hasValidOption = data.options.some((option) => option.trim() !== "");
    setIsFormValid(!hasOptions || hasValidOption);
  }, [hasOptions]);

  const currentFieldData = fieldData || null;

  const isSubmitting =
    updateOptionsMutation.isPending ||
    scopedBulkMutation.isPending ||
    updateFieldMutation.isPending;

  // Hide/show flips local state only; persisted via isHidden on the next Save.
  const handleHiddenToggle = useCallback((id: number | string) => {
    setFormState((prev) => ({
      ...prev,
      optionsManagerData: {
        ...prev.optionsManagerData,
        optionItems: (prev.optionsManagerData.optionItems || []).map((item) =>
          item.id === id ? { ...item, isHidden: !item.isHidden } : item
        )
      }
    }));
  }, []);

  const handleFormSubmit = useCallback(async () => {
    // Wait for the flag to resolve so an early submit doesn't drop defaultValue.
    if (
      isSubmitting ||
      defaultEntryLoading ||
      configurableSystemFieldsLoading ||
      !fieldData
    ) {
      throw new Error("Form is already being submitted or no field data");
    }

    try {
      const optionItems = formState.optionsManagerData.optionItems;
      const options = formState.optionsManagerData.options;
      const selectedParentId =
        formState.optionsManagerData.selectedParentId ?? null;

      // For renamed values still in use, ask how records are handled: "keep"
      // leaves the old value and adds the new name; "replace" renames in place.
      const keepExistingIds = new Set<number | string>();
      if (
        configurableSystemFieldsEnabled &&
        baselineNamesRef.current &&
        lookupConfig
      ) {
        // Scoped fields can't resolve usage until a parent is chosen — skip the
        // check then (there's nothing renameable without a loaded value list).
        const canCheckUsage = !lookupConfig.parent || selectedParentId !== null;
        for (let index = 0; canCheckUsage && index < options.length; index++) {
          const existingId = optionItems?.[index]?.id;
          if (existingId === undefined) continue;
          const newName = options[index].trim();
          const oldName = baselineNamesRef.current.get(existingId);
          if (oldName === undefined || oldName === newName || newName === "") {
            continue;
          }
          const usageCount = await fetchUsage(
            lookupConfig.usageRoute,
            lookupConfig.buildUsagePathParams(existingId, selectedParentId)
          );
          if (usageCount === 0) continue; // unused — safe to rename in place
          const strategy = await promptRename(oldName, newName);
          if (strategy === "cancel") throw new Error(RENAME_CANCELLED);
          if (strategy === "keep") keepExistingIds.add(existingId);
        }
      }

      // Persist field-level settings — after the rename gate, so a cancelled
      // prompt aborts before any write. Only these two keys; only when changed.
      if (
        formState.isRequired !== fieldData.required ||
        formState.showInCreation !== fieldData.showInCreation
      ) {
        await updateFieldMutationRef.current.mutateAsync({
          data: {
            id: fieldData.id,
            required: formState.isRequired,
            showInCreation: formState.showInCreation
          },
          pathParams: {
            entityType: entityTypeToEntityName[entityType],
            fieldId: fieldData.id
          }
        });
      }

      if (!hasOptions) {
        return;
      }

      // Build the bulk payload. A "keep" value emits two entries: the original
      // (unchanged) plus the new name as a brand-new value.
      const payload: EditableSystemFieldOption[] = [];
      options.forEach((optionName, index) => {
        const trimmed = optionName.trim();
        if (trimmed === "") return;
        const existingItem = optionItems?.[index];
        const existingId = existingItem?.id;
        const isHidden = existingItem?.isHidden;
        const withHidden = (entry: EditableSystemFieldOption) => {
          // isHidden only for lookup fields, flag-on. Flag-off omits it so the
          // BE preserves stored visibility (exclude_unset).
          if (
            configurableSystemFieldsEnabled &&
            lookupConfig &&
            isHidden !== undefined
          ) {
            entry.isHidden = isHidden;
          }
          return entry;
        };
        if (existingId !== undefined && keepExistingIds.has(existingId)) {
          const originalName =
            baselineNamesRef.current?.get(existingId) ?? trimmed;
          payload.push(withHidden({ id: existingId, name: originalName }));
          payload.push({ name: trimmed });
          return;
        }
        payload.push(
          withHidden(
            existingId !== undefined
              ? { id: existingId, name: trimmed }
              : { name: trimmed }
          )
        );
      });

      // Re-baseline to the just-saved names for the next edit.
      const syncBaseline = () => {
        const baseline = baselineNamesRef.current ?? new Map();
        payload.forEach((entry) => {
          if (entry.id !== undefined) baseline.set(entry.id, entry.name);
        });
        baselineNamesRef.current = baseline;
      };

      // Parent-scoped fields (e.g. sub-industries) save to their nested bulk
      // endpoint; path-param from the registry. Flag-independent, like the panel.
      if (lookupConfig?.parent) {
        // No parent chosen ⇒ nothing to bulk-save; field-level settings above
        // already saved, so return cleanly rather than failing.
        if (selectedParentId === null) {
          return;
        }
        await scopedBulkMutationRef.current.mutateAsync({
          data: payload,
          pathParams: {
            [lookupConfig.parent.pathParam]: String(selectedParentId)
          }
        });
        syncBaseline();
        return;
      }

      // fieldKey → snake_case plural path; strip trailing _id ("industryId" → "industries").
      const snakeCaseBase = snakeCase(fieldData.fieldKey).replace(/_id$/, "");
      // consonant + y → ies (industry → industries); vowel + y → +s (survey → surveys).
      const snakeCaseFieldKey = /[^aeiou]y$/.test(snakeCaseBase)
        ? snakeCaseBase.slice(0, -1) + "ies"
        : snakeCaseBase + "s";

      // Fall back to "no default" if the default no longer points at a live option.
      const normalizedDefaultValue =
        formState.optionsManagerData.defaultValue?.trim() || "";
      const defaultValueExists = payload.some(
        (option) => option.name === normalizedDefaultValue
      );

      const data = defaultEntryEnabled
        ? {
            options: payload,
            defaultValue: defaultValueExists
              ? normalizedDefaultValue
              : NONE_OPTION_VALUE
          }
        : payload;

      await updateOptionsMutationRef.current.mutateAsync({
        data,
        pathParams: {
          entityType: entityTypeToEntityName[entityType],
          fieldKey: snakeCaseFieldKey
        }
      });
      syncBaseline();
    } catch (error) {
      throw error;
    }
  }, [
    formState,
    fieldData,
    isSubmitting,
    entityType,
    defaultEntryEnabled,
    defaultEntryLoading,
    configurableSystemFieldsEnabled,
    configurableSystemFieldsLoading,
    fetchUsage,
    promptRename,
    lookupConfig,
    hasOptions
  ]);

  const handleSubmitButton = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const submitError = updateOptionsMutation.error;

  return {
    formState,
    currentFieldData,
    isFormValid,
    formRef,
    formSetValue,
    setFormData,
    setOptionsManagerData,
    setIsRequired,
    setShowInCreation,
    setShowHelpText,
    setFormSetValue,
    handleFormValid,
    handleOptionsManagerChange,
    handleHiddenToggle,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting,
    submitError,
    renamePrompt,
    resolveRename
  };
};
